import React from 'react';

import { confirmDestructive } from '../common/confirmDestructive';
import { FullItemList } from './TagRulePass';
import { RULE_LIMITS, STRICT_WORDS, countWords } from '../../utils/tagRules';

/**
 * The two questions an automatic tag asks before it takes tags off items
 * (docs/plans/tags.md §3.4, §9 "Strict removal is destructive"; ruled d11).
 * Both are the platform's destructive confirm (red button, Cancel focused),
 * and both name the COUNT the API's dry run answered. Each resolves true to
 * go ahead, false not to.
 */

/**
 * Turning on "Only this rule applies this tag" while people have applied the
 * tag by hand: those applications come off. `changed` says the count moved
 * since the person last looked (the real call answered 409
 * `confirm_required` with a new count).
 */
export function confirmTagRuleStrict(modal, { tagName = 'this tag', count = 0, changed = false } = {}) {
	return new Promise(resolve => {
		confirmDestructive(modal, {
			title: `Take “${tagName}” off ${countWords(count, 'item')}?`,
			content: (
				<p>
					{changed ? 'The count changed since you last looked. ' : ''}
					{`${STRICT_WORDS.strict}, so “${tagName}” comes off the ${countWords(count, 'item')} people tagged by hand. `}
					Each removal is kept in History, and can be undone once people may apply the tag again.
				</p>
			),
			okText: `Take it off ${countWords(count, 'item')}`,
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}

/**
 * "Stop applying by rule": the rule is removed and the tag becomes one people
 * apply by hand. Its automatic applications become hand ones; an item that
 * already carries 50 hand tags loses the tag instead. Those are NAMED here,
 * before the person decides: `fullItems`, the dry run's `fullIds` (the first
 * 20 the caller can read, keepr-api T6b) read as items, as record badges the
 * way the pass's final report names them (FullItemList), "And N more." for
 * the rest of `full`.
 */
export function confirmTagRuleStop(modal, { tagName = 'this tag', movesToHand = 0, full = 0, finishing = false, fullItems = [], cardsById = {} } = {}) {
	const moves = Number(movesToHand) || 0;
	const lost = Number(full) || 0;
	const kept = Math.max(moves - lost, 0);
	return new Promise(resolve => {
		confirmDestructive(modal, {
			// `finishing`: the rule is already gone, but items still carry the
			// tag by rule (a stop that did not finish) — the same act ends it.
			title: finishing ? `Finish stopping “${tagName}” by rule?` : `Stop applying “${tagName}” by rule?`,
			content: (
				<>
					<p>{finishing
						? `“${tagName}” has no rule any more, but ${countWords(moves, 'item')} still ${moves === 1 ? 'carries' : 'carry'} it from the rule it had.`
						: `The rule is removed, and “${tagName}” becomes a tag people put on by hand.`}</p>
					<p>
						{moves
							? `It stays on the ${countWords(kept, 'item')} the rule tagged, as tags people can take off.`
							: 'The rule has not tagged any items, so no item changes.'}
					</p>
					{lost ? (
						<>
							<p>
								{`${countWords(lost, 'item')} already ${lost === 1 ? 'has' : 'have'} ${RULE_LIMITS.handTags} tags, the most an item can hold, so ${lost === 1 ? 'it loses' : 'they lose'} “${tagName}”${fullItems.length ? ':' : '.'}`}
							</p>
							<FullItemList items={fullItems} count={lost} cardsById={cardsById} link={false} label={`Items that lose “${tagName}”`} />
						</>
					) : null}
				</>
			),
			okText: finishing ? 'Finish stopping' : 'Stop applying by rule',
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}
